import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../hooks/useAuthStore';

// ── 100ms Prebuilt config ─────────────────────────────────────────────────
const HMS_SUBDOMAIN    = 'sameer-videoconf-2311';
const HMS_DEFAULT_ROOM = 'VC-patient-king-451830';
const HMS_BASE_URL     = `https://${HMS_SUBDOMAIN}.app.100ms.live/meeting`;
const API_BASE         = '/api';

/* ── Design tokens ─────────────────────────────────────────────────────── */
const C = {
  primary:            '#4800b2',
  primaryContainer:   '#6200ee',
  onPrimary:          '#ffffff',
  primaryFixed:       '#e8ddff',
  primaryFixedDim:    '#cfbdff',
  surface:            '#f8f9fd',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow:    '#f2f3f7',
  surfaceContainerHigh:   '#e7e8ec',
  secondaryContainer: '#e3e0f4',
  secondary:          '#5e5d6d',
  onSurface:          '#191c1f',
  onSurfaceVariant:   '#494456',
  outline:            '#7a7488',
  outlineVariant:     '#cbc3d9',
  error:              '#ba1a1a',
  green:              '#2e7d32',
  greenHover:         '#1b5e20',
  greenLight:         '#dcfce7',
  blue:               '#0284c7',
}

const formatDuration = (s) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
};

const getSupportedMimeType = () => {
  const types = ['audio/webm;codecs=opus','audio/webm','audio/ogg;codecs=opus','audio/ogg','audio/mp4'];
  for (const t of types) { if (MediaRecorder.isTypeSupported(t)) return t; }
  return '';
};

export default function VideoChat() {
  const { user } = useAuthStore();

  const [meetingState, setMeetingState]   = useState('idle');
  const [roomCode, setRoomCode]           = useState('');
  const [customRoom, setCustomRoom]       = useState('');
  const [error, setError]                 = useState('');
  const [copied, setCopied]               = useState(false);
  const [meetingDuration, setMeetingDuration] = useState(0);
  const [creatingRoom, setCreatingRoom]   = useState(false);
  const durationTimerRef = useRef(null);

  const [isTranscribing, setIsTranscribing]   = useState(false);
  const [transcript, setTranscript]           = useState([]);
  const [transcriptError, setTranscriptError] = useState('');
  const [isProcessing, setIsProcessing]       = useState(false);
  const mediaRecorderRef  = useRef(null);
  const micStreamRef      = useRef(null);
  const transcriptEndRef  = useRef(null);
  const chunkIndexRef     = useRef(0);
  const isTranscribingRef = useRef(false);

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail]       = useState('');
  const [inviteName, setInviteName]         = useState('');
  const [sendingInvite, setSendingInvite]   = useState(false);
  const [inviteSent, setInviteSent]         = useState(false);
  const [inviteError, setInviteError]       = useState('');

  const appLink  = roomCode ? `${window.location.origin}/video-chat?room=${roomCode}` : '';
  const isDoctor = ['doctor','admin','ceo','clinic_manager'].includes(user?.role);
  const iframeSrc = roomCode ? `${HMS_BASE_URL}/${roomCode}` : '';

  useEffect(() => {
    if (meetingState === 'active') {
      durationTimerRef.current = setInterval(() => setMeetingDuration(p => p + 1), 1000);
    } else { clearInterval(durationTimerRef.current); }
    return () => clearInterval(durationTimerRef.current);
  }, [meetingState]);

  useEffect(() => { transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [transcript]);
  useEffect(() => { if (meetingState !== 'active') stopTranscription(); }, [meetingState]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get('room'); if (r) setCustomRoom(r);
  }, []);

  const sendToWhisper = async (blob) => {
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (!apiKey) { setTranscriptError('OpenAI API key not configured'); return; }
    if (blob.size < 1000) return;
    setIsProcessing(true);
    try {
      const formData = new FormData();
      const ext = blob.type.includes('ogg') ? 'ogg' : blob.type.includes('mp4') ? 'mp4' : 'webm';
      formData.append('file', blob, `audio.${ext}`);
      formData.append('model', 'whisper-1');
      formData.append('language', 'en');
      const res = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST', headers: { Authorization: `Bearer ${apiKey}` }, body: formData,
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); setTranscriptError(`Whisper error: ${err?.error?.message || res.status}`); return; }
      const data = await res.json();
      const text = data.text?.trim();
      if (text) {
        setTranscript(prev => [...prev, {
          id: `${Date.now()}-${chunkIndexRef.current++}`,
          speaker: user?.name || user?.email?.split('@')[0] || 'You',
          text, timestamp: new Date(),
        }]);
        setTranscriptError('');
      }
    } catch (err) { console.error('[Whisper]', err); }
    finally { setIsProcessing(false); }
  };

  const startChunk = (stream) => {
    if (!isTranscribingRef.current) return;
    const mimeType = getSupportedMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 1000) sendToWhisper(e.data); };
    recorder.onerror = () => {};
    recorder.onstart  = () => { setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, 6000); };
    recorder.onstop   = () => { if (isTranscribingRef.current && stream.active) startChunk(stream); };
    recorder.start();
  };

  const startTranscription = async () => {
    setTranscriptError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      micStreamRef.current = stream;
      isTranscribingRef.current = true;
      setIsTranscribing(true);
      setTranscript(prev => [...prev, { id: `sys-${Date.now()}`, speaker: 'System', text: '🎙️ Live transcription started — speaking every few seconds will be transcribed.', timestamp: new Date(), isSystem: true }]);
      startChunk(stream);
    } catch (err) { setTranscriptError(`Microphone access denied: ${err.message}`); }
  };

  const stopTranscription = () => {
    isTranscribingRef.current = false;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') { try { mediaRecorderRef.current.stop(); } catch {} }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null; }
    mediaRecorderRef.current = null;
    setIsTranscribing(false);
  };

  const clearTranscript = () => setTranscript([]);

  const copyTranscript = async () => {
    const text = transcript.filter(t => !t.isSystem).map(t => `[${t.timestamp.toLocaleTimeString()}] ${t.speaker}: ${t.text}`).join('\n\n');
    try { await navigator.clipboard.writeText(text); } catch { const el = document.createElement('textarea'); el.value = text; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
    alert('Transcript copied!');
  };

  const downloadTranscript = () => {
    const text = transcript.filter(t => !t.isSystem).map(t => `[${t.timestamp.toLocaleTimeString()}] ${t.speaker}: ${t.text}`).join('\n\n');
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([text], { type: 'text/plain' })), download: `transcript-${roomCode}-${new Date().toISOString().split('T')[0]}.txt` });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const startMeeting = (room) => { setError(''); setMeetingDuration(0); setTranscript([]); setTranscriptError(''); setRoomCode(room); setMeetingState('active'); };

  const handleCreateMeeting = async () => {
    setError(''); setCreatingRoom(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const res = await fetch('/api/video/hms-room.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ consultation_id: `${user?.id || 'guest'}-${Date.now()}`, doctor_name: user?.name || user?.email?.split('@')[0] || 'Doctor', patient_name: 'Patient' }),
      });
      const data = await res.json();
      if (data.success && data.room_code) startMeeting(data.room_code);
      else if (data.success && data.room_id) startMeeting(data.room_id);
      else { console.warn('[HMS] Room creation failed, using default room:', data.error); startMeeting(HMS_DEFAULT_ROOM); }
    } catch (err) { console.warn('[HMS] Room creation error, using default room:', err.message); startMeeting(HMS_DEFAULT_ROOM); }
    finally { setCreatingRoom(false); }
  };

  const handleJoinMeeting = () => {
    let room = customRoom.trim();
    if (!room) { setError('Please enter a room code or meeting link'); return; }
    if (room.includes('video-chat?room=')) room = room.split('video-chat?room=')[1];
    else if (room.includes(`${HMS_SUBDOMAIN}.app.100ms.live/meeting/`)) room = room.split(`${HMS_SUBDOMAIN}.app.100ms.live/meeting/`)[1];
    startMeeting(room);
  };

  const handleEndMeeting = () => { stopTranscription(); clearInterval(durationTimerRef.current); setMeetingState('ended'); };

  const handleNewMeeting = () => {
    setMeetingState('idle'); setRoomCode(''); setCustomRoom(''); setMeetingDuration(0); setError('');
    setTranscript([]); setTranscriptError(''); setShowInviteForm(false); setInviteEmail(''); setInviteName(''); setInviteSent(false); setInviteError('');
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); }
    catch { const el = document.createElement('textarea'); el.value = text; document.body.appendChild(el); el.select(); document.execCommand('copy'); document.body.removeChild(el); }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const sendEmailInvite = async () => {
    if (!inviteEmail.trim()) { setInviteError("Please enter the patient's email address"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) { setInviteError('Please enter a valid email address'); return; }
    setSendingInvite(true); setInviteError(''); setInviteSent(false);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';
      const res = await fetch(`${API_BASE}/video/send-invite.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ patient_email: inviteEmail.trim(), patient_name: inviteName.trim() || 'Patient', meeting_link: appLink, room_name: roomCode, doctor_name: user?.name || user?.email?.split('@')[0] || 'Your Doctor', scheduled_time: '' }),
      });
      const data = await res.json();
      if (data.success) { setInviteSent(true); setInviteEmail(''); setInviteName(''); setTimeout(() => setInviteSent(false), 6000); }
      else { setInviteError(data.error || 'Failed to send email invite'); }
    } catch (err) { setInviteError(`Network error: ${err.message}`); }
    finally { setSendingInvite(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif", color: C.onSurface }}>

      {/* ── Hero Banner ────────────────────────────────────────────────── */}
      <section style={{
        position: 'relative', overflow: 'hidden', borderRadius: 16,
        background: `linear-gradient(135deg, ${C.primary}, ${C.primaryContainer})`,
        padding: '40px 40px', color: '#fff', marginBottom: 32, boxShadow: '0 8px 32px rgba(72,0,178,0.25)',
      }}>
        <div style={{ position: 'absolute', top: 0, right: 0, marginRight: -80, marginTop: -80, width: 384, height: 384, background: 'rgba(255,255,255,0.08)', borderRadius: '50%', filter: 'blur(48px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 24 }}>
          <div style={{ maxWidth: 560 }}>
            <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: C.primaryFixedDim, marginBottom: 6 }}>Telehealth Platform</div>
            <h1 style={{ fontSize: 32, fontWeight: 800, fontFamily: "'Manrope',sans-serif", margin: '0 0 12px' }}>Video Chat</h1>
            <p style={{ fontSize: 16, color: C.primaryFixedDim, margin: '0 0 18px', lineHeight: 1.6 }}>
              Secure video consultation with live AI transcript — powered by 100ms &amp; OpenAI Whisper
            </p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)', padding: '6px 16px', borderRadius: 9999, fontSize: 12, fontWeight: 600, border: '1px solid rgba(255,255,255,0.2)' }}>
              <i className="uil uil-shield-check" style={{ fontSize: 14 }} />
              HD video · end-to-end encrypted
            </div>
          </div>
          <div style={{ width: 112, height: 112, background: 'rgba(255,255,255,0.1)', borderRadius: 20, backdropFilter: 'blur(16px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
            <i className="uil uil-video" style={{ fontSize: 56, opacity: 0.5, color: '#fff' }} />
          </div>
        </div>
      </section>

      {/* Error */}
      {error && (
        <div style={{ background: '#fef2f2', borderLeft: `3px solid ${C.error}`, padding: '10px 18px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.error, marginBottom: 20 }}>
          <i className="uil uil-exclamation-circle" style={{ fontSize: 16, flexShrink: 0 }} />
          {error}
        </div>
      )}

      {/* ── IDLE STATE ─────────────────────────────────────────────────────── */}
      {meetingState === 'idle' && (
        <>
          {/* Two main action cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginBottom: 32 }}>
            {/* Start New Meeting */}
            <div style={{ background: C.surfaceContainerLowest, padding: 32, borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${C.outlineVariant}33` }}>
              <div style={{ width: 56, height: 56, background: C.secondaryContainer, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <i className="uil uil-plus-square" style={{ fontSize: 28, color: C.primaryContainer }} />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Manrope',sans-serif", margin: '0 0 8px' }}>Start New Meeting</h2>
              <p style={{ color: C.onSurfaceVariant, fontSize: 14, margin: '0 0 28px', lineHeight: 1.5 }}>Open a consultation room and invite your patient</p>
              <button
                onClick={handleCreateMeeting}
                disabled={creatingRoom}
                style={{
                  width: '100%', padding: '14px', background: creatingRoom ? '#4ade80' : C.green, color: '#fff',
                  border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 16, cursor: creatingRoom ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  boxShadow: '0 4px 16px rgba(46,125,50,0.3)', transition: 'all 0.15s',
                }}
              >
                {creatingRoom ? (
                  <><span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Creating room…</>
                ) : (
                  <><i className="uil uil-video" style={{ fontSize: 20 }} /> Start Video Meeting</>
                )}
              </button>
            </div>

            {/* Join a Meeting */}
            <div style={{ background: C.surfaceContainerLowest, padding: 32, borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${C.outlineVariant}33` }}>
              <div style={{ width: 56, height: 56, background: C.secondaryContainer, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <i className="uil uil-link" style={{ fontSize: 28, color: C.primaryContainer }} />
              </div>
              <h2 style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Manrope',sans-serif", margin: '0 0 8px' }}>Join a Meeting</h2>
              <p style={{ color: C.onSurfaceVariant, fontSize: 14, margin: '0 0 28px', lineHeight: 1.5 }}>Enter a room code or paste a meeting link</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input
                  type="text" value={customRoom} onChange={e => setCustomRoom(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleJoinMeeting(); }}
                  placeholder="https://...video-chat?room=... or room code"
                  style={{ width: '100%', padding: '14px 16px', background: C.surfaceContainerLow, border: 'none', borderRadius: 12, fontSize: 14, color: C.onSurface, outline: 'none', boxSizing: 'border-box' }}
                />
                <button
                  onClick={handleJoinMeeting}
                  disabled={!customRoom.trim()}
                  style={{
                    width: '100%', padding: '14px', background: !customRoom.trim() ? C.outlineVariant : C.primaryContainer, color: '#fff',
                    border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 16,
                    cursor: !customRoom.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    boxShadow: !customRoom.trim() ? 'none' : '0 4px 16px rgba(98,0,238,0.3)',
                    transition: 'all 0.15s',
                  }}
                >
                  <i className="uil uil-signin" style={{ fontSize: 20 }} /> Join Meeting
                </button>
              </div>
            </div>
          </div>

          {/* Bento info section */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
            {/* How to use */}
            <div style={{ background: C.surfaceContainerLow, padding: 32, borderRadius: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Manrope',sans-serif", margin: '0 0 28px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="uil uil-info-circle" style={{ color: C.primaryContainer, fontSize: 20 }} />
                How to use Video Chat
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                {[
                  { n: 1, title: 'Doctor starts meeting', desc: 'Launch the session through the green button above to initialize the secure portal.' },
                  { n: 2, title: 'Email patient the link', desc: 'Copy the unique room URL and send it to your patient via secure email or SMS.' },
                  { n: 3, title: 'Patient joins', desc: 'The patient enters the waiting room. You admit them to start the consultation.' },
                ].map(({ n, title, desc }) => (
                  <div key={n} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.primaryContainer, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 15 }}>{n}</div>
                    <p style={{ fontWeight: 600, fontSize: 13, margin: 0 }}>{title}</p>
                    <p style={{ fontSize: 12, color: C.onSurfaceVariant, margin: 0, lineHeight: 1.6 }}>{desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* AI Transcript card */}
            <div style={{ background: C.surfaceContainerLowest, padding: 32, borderRadius: 16, border: `1px solid ${C.primary}1a`, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 8, right: 8 }}>
                <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, background: C.primaryContainer, color: '#fff', padding: '3px 8px', borderRadius: 4 }}>AI Enhanced</span>
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Manrope',sans-serif", margin: '0 0 12px' }}>Live AI Transcript</h3>
              <p style={{ fontSize: 13, color: C.onSurfaceVariant, lineHeight: 1.6, margin: '0 0 20px' }}>
                During the call, click the <strong style={{ color: C.primary }}>"Start Transcript"</strong> button. InstantDr will generate a live, medical-grade transcript of the conversation in real-time.
              </p>
              <div style={{ padding: 14, background: `${C.primary}0d`, borderRadius: 10, border: `1px solid ${C.primary}1a` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, fontWeight: 700, color: C.primary, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.primary, display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
                  Whisper V3 Active
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Notice */}
          <div style={{ background: '#fff9c4', border: '1px solid rgba(251,192,45,0.3)', borderRadius: 14, padding: '18px 22px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <i className="uil uil-exclamation-triangle" style={{ fontSize: 22, color: '#f57f17', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p style={{ fontWeight: 700, color: '#f57f17', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 6px' }}>Privacy Notice</p>
              <p style={{ fontSize: 13, color: C.onSurface, margin: 0, lineHeight: 1.6 }}>
                Consultations are end-to-end encrypted. Video consultations use <strong>100ms</strong> — a secure WebRTC platform. Live transcription sends microphone audio to OpenAI Whisper for processing. Rooms are secured with unique codes.
              </p>
            </div>
          </div>
        </>
      )}

      {/* ── ACTIVE MEETING ─────────────────────────────────────────────────── */}
      {meetingState === 'active' && (
        <>
          {/* Active session bar */}
          <div style={{ background: C.surfaceContainerLowest, borderRadius: 14, padding: '14px 20px', marginBottom: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: `1px solid ${C.outlineVariant}44` }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#dcfce7', borderRadius: 9999, padding: '4px 12px' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', animation: 'pulse 1.5s infinite', display: 'inline-block' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d' }}>Live</span>
                </div>
                <span style={{ fontSize: 13, color: C.onSurfaceVariant, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i className="uil uil-clock" style={{ fontSize: 14 }} />{formatDuration(meetingDuration)}
                </span>
                <span style={{ fontSize: 11, color: C.outlineVariant, fontFamily: 'monospace', display: 'none' }}>{roomCode}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => copyToClipboard(appLink)} style={{
                  padding: '6px 14px', background: copied ? '#dcfce7' : C.surfaceContainerHigh, color: copied ? '#16a34a' : C.onSurfaceVariant,
                  border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <i className={`uil ${copied ? 'uil-check' : 'uil-clipboard'}`} style={{ fontSize: 14 }} />
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
                {isDoctor && (
                  <button onClick={() => { setShowInviteForm(v => !v); setInviteError(''); setInviteSent(false); }} style={{
                    padding: '6px 14px', background: showInviteForm ? C.primaryFixed : C.surfaceContainerHigh, color: showInviteForm ? C.primary : C.onSurfaceVariant,
                    border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                  }}>
                    <i className="uil uil-envelope" style={{ fontSize: 14 }} /> Email Patient
                  </button>
                )}
                <button onClick={handleEndMeeting} style={{
                  padding: '6px 14px', background: '#fee2e2', color: C.error, border: 'none', borderRadius: 8,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <i className="uil uil-phone-times" style={{ fontSize: 14 }} /> End
                </button>
              </div>
            </div>

            {/* Email invite form */}
            {showInviteForm && isDoctor && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.outlineVariant}44` }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: C.primaryContainer, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="uil uil-envelope" style={{ fontSize: 14 }} /> Send meeting link to patient via email
                </p>
                {inviteSent && (
                  <div style={{ background: '#dcfce7', color: '#15803d', padding: '8px 14px', borderRadius: 8, marginBottom: 10, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="uil uil-check-circle" style={{ fontSize: 16 }} /> Email invite sent! Patient will receive the meeting link shortly.
                  </div>
                )}
                {inviteError && (
                  <div style={{ background: '#fef2f2', color: C.error, padding: '8px 14px', borderRadius: 8, marginBottom: 10, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <i className="uil uil-exclamation-circle" style={{ fontSize: 14 }} /> {inviteError}
                  </div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <input type="text" placeholder="Patient name (optional)" value={inviteName} onChange={e => setInviteName(e.target.value)}
                    style={{ flex: '1 1 160px', padding: '8px 12px', background: C.surfaceContainerLow, border: 'none', borderRadius: 8, fontSize: 13, color: C.onSurface, outline: 'none' }} />
                  <input type="email" placeholder="Patient email address *" value={inviteEmail}
                    onChange={e => { setInviteEmail(e.target.value); setInviteError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter') sendEmailInvite(); }}
                    style={{ flex: '2 1 220px', padding: '8px 12px', background: C.surfaceContainerLow, border: 'none', borderRadius: 8, fontSize: 13, color: C.onSurface, outline: 'none' }} />
                  <button onClick={sendEmailInvite} disabled={sendingInvite || !inviteEmail.trim()} style={{
                    padding: '8px 18px', background: sendingInvite || !inviteEmail.trim() ? C.outlineVariant : C.primaryContainer, color: '#fff',
                    border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: sendingInvite || !inviteEmail.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
                  }}>
                    {sendingInvite ? <><span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> Sending…</> : <><i className="uil uil-message" style={{ fontSize: 13 }} /> Send Invite</>}
                  </button>
                </div>
                <p style={{ fontSize: 11, color: C.onSurfaceVariant, margin: '6px 0 0' }}>
                  <i className="uil uil-info-circle" style={{ fontSize: 12 }} /> Patient receives an email from <strong>instantdrconsult@optimail.pro</strong> with the meeting link.
                </p>
              </div>
            )}
          </div>

          {/* Video + Transcript */}
          <div style={{ display: 'grid', gridTemplateColumns: (isTranscribing || transcript.length > 0) ? '1fr minmax(280px, 360px)' : '1fr', gap: 18, marginBottom: 18 }}>
            <div style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', background: '#111', height: 560 }}>
              <iframe src={iframeSrc} allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} title="Video Consultation — 100ms" />
            </div>

            {(isTranscribing || transcript.length > 0) && (
              <div style={{ background: C.surfaceContainerLowest, borderRadius: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column', maxHeight: 560, overflow: 'hidden', border: `1px solid ${C.outlineVariant}44` }}>
                {/* Transcript header */}
                <div style={{ padding: '12px 16px', background: `linear-gradient(135deg, ${C.green}, ${C.blue})`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <i className="uil uil-file-alt" style={{ color: '#fff', fontSize: 16 }} />
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>Live Transcript</span>
                    {isTranscribing && (
                      <span style={{ background: C.error, color: '#fff', borderRadius: 9999, padding: '2px 8px', fontSize: 10, fontWeight: 700, animation: 'pulse 1s infinite' }}>● REC</span>
                    )}
                    {isProcessing && <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />}
                  </div>
                  {transcript.filter(t => !t.isSystem).length > 0 && (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[{ icon: 'uil-clipboard', fn: copyTranscript, title: 'Copy' }, { icon: 'uil-import', fn: downloadTranscript, title: 'Download' }, { icon: 'uil-trash-alt', fn: clearTranscript, title: 'Clear' }].map(({ icon, fn, title }) => (
                        <button key={icon} onClick={fn} title={title} style={{ padding: '4px 8px', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#fff', display: 'flex' }}>
                          <i className={`uil ${icon}`} style={{ fontSize: 13 }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {transcriptError && (
                  <div style={{ background: '#fef2f2', color: C.error, padding: '8px 14px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <i className="uil uil-exclamation-circle" style={{ fontSize: 14 }} /> {transcriptError}
                  </div>
                )}
                <div style={{ flex: 1, overflowY: 'auto', padding: 14, scrollbarWidth: 'thin' }}>
                  {transcript.length === 0 && isTranscribing && (
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                      <span style={{ width: 28, height: 28, border: `3px solid ${C.outlineVariant}`, borderTop: `3px solid ${C.green}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block', marginBottom: 12 }} />
                      <p style={{ fontSize: 12, color: C.onSurfaceVariant, margin: 0 }}>Listening… speak clearly into your microphone</p>
                    </div>
                  )}
                  {transcript.map(entry => (
                    <div key={entry.id} style={{ marginBottom: 14, textAlign: entry.isSystem ? 'center' : 'left' }}>
                      {entry.isSystem ? (
                        <small style={{ color: C.onSurfaceVariant, fontStyle: 'italic', fontSize: 11 }}>{entry.text}</small>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: C.green, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                              {entry.speaker.charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 600, color: C.green }}>{entry.speaker}</span>
                            <span style={{ fontSize: 10, color: C.onSurfaceVariant }}>{entry.timestamp.toLocaleTimeString()}</span>
                          </div>
                          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', marginLeft: 30 }}>
                            <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5 }}>{entry.text}</p>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  <div ref={transcriptEndRef} />
                </div>
                {transcript.filter(t => !t.isSystem).length > 0 && (
                  <div style={{ padding: '8px 14px', borderTop: `1px solid ${C.outlineVariant}44`, fontSize: 11, color: C.onSurfaceVariant, flexShrink: 0 }}>
                    <i className="uil uil-chat" style={{ fontSize: 12, marginRight: 4 }} />
                    {transcript.filter(t => !t.isSystem).length} segment(s) transcribed
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Transcript controls */}
          <div style={{ background: C.surfaceContainerLowest, borderRadius: 14, padding: '14px 20px', marginBottom: 18, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: `1px solid ${C.outlineVariant}44` }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="uil uil-microphone" style={{ color: C.green, fontSize: 20 }} />
                <span style={{ fontWeight: 600, fontSize: 13 }}>Live Transcript (OpenAI Whisper)</span>
              </div>
              {!isTranscribing ? (
                <button onClick={startTranscription} style={{ padding: '7px 16px', background: C.green, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="uil uil-record-audio" style={{ fontSize: 14 }} /> Start Transcript
                </button>
              ) : (
                <button onClick={stopTranscription} style={{ padding: '7px 16px', background: C.error, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <i className="uil uil-stop-circle" style={{ fontSize: 14 }} /> Stop Transcript
                </button>
              )}
              {isTranscribing && (
                <span style={{ fontSize: 12, color: C.green, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.green, animation: 'pulse 1s infinite', display: 'inline-block' }} />
                  Recording in 6-second chunks — auto-transcribing
                </span>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── MEETING ENDED ──────────────────────────────────────────────────── */}
      {meetingState === 'ended' && (
        <div style={{ background: C.surfaceContainerLowest, borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 24 }}>
          <div style={{ width: 88, height: 88, background: C.surfaceContainerHigh, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <i className="uil uil-phone-times" style={{ fontSize: 40, color: C.onSurfaceVariant }} />
          </div>
          <h4 style={{ fontWeight: 700, fontFamily: "'Manrope',sans-serif", margin: '0 0 8px', fontSize: 24 }}>Meeting Ended</h4>
          {meetingDuration > 0 && <p style={{ color: C.onSurfaceVariant, fontSize: 14, marginBottom: 24 }}>Duration: {formatDuration(meetingDuration)}</p>}

          {transcript.filter(t => !t.isSystem).length > 0 && (
            <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 28, textAlign: 'left', maxWidth: 480, margin: '0 auto 24px' }}>
              <i className="uil uil-file-alt" style={{ fontSize: 24, color: C.green, flexShrink: 0 }} />
              <div>
                <strong style={{ fontSize: 14 }}>Transcript available!</strong>
                <p style={{ fontSize: 13, margin: '4px 0 10px', color: C.onSurfaceVariant }}>{transcript.filter(t => !t.isSystem).length} segment(s) captured</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={downloadTranscript} style={{ padding: '6px 14px', background: C.green, color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <i className="uil uil-import" style={{ fontSize: 13 }} /> Download .txt
                  </button>
                  <button onClick={copyTranscript} style={{ padding: '6px 14px', background: '#f0fdf4', color: C.green, border: `1px solid #bbf7d0`, borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <i className="uil uil-clipboard" style={{ fontSize: 13 }} /> Copy
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
            <button onClick={handleNewMeeting} style={{ padding: '12px 28px', background: C.green, color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="uil uil-video" style={{ fontSize: 18 }} /> New Meeting
            </button>
            <button onClick={() => setMeetingState('active')} style={{ padding: '12px 28px', background: C.primaryContainer, color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="uil uil-redo" style={{ fontSize: 18 }} /> Rejoin Room
            </button>
          </div>
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes pulse  { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}